import { Plus, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../utils/cn';
import { Button } from '../Button/Button';
import {
  GUIDELINE_MAX_CHARS,
  GUIDELINES_MAX_ITEMS,
  GUIDELINES_MAX_TOTAL_CHARS,
} from './aiCommandsList.constants';

const WARN_RATIO = 0.9;

// Count Unicode code points to match Python's `len` on the API.
export const countChars = (text: string) => Array.from(text).length;

interface AiCommandsListProps {
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  error?: boolean;
}

export const AiCommandsList = ({
  value = [],
  onChange,
  disabled = false,
  error = false,
}: AiCommandsListProps) => {
  const { t } = useTranslation(['knowledge_base']);
  const textareaRefs = useRef<(HTMLTextAreaElement | null)[]>([]);
  const footerRef = useRef<HTMLDivElement>(null);
  const totalChars = countChars(value.join(''));
  const [droppedPasteLines, setDroppedPasteLines] = useState(0);

  const focusTextarea = (index: number, cursorPosition: number) => {
    requestAnimationFrame(() => {
      const el = textareaRefs.current[index];
      if (el) {
        el.focus();
        el.setSelectionRange(cursorPosition, cursorPosition);
      }
    });
  };

  const handleUpdate = (index: number, newText: string) => {
    setDroppedPasteLines(0);
    const newValue = [...value];
    newValue[index] = newText;
    onChange(newValue);
  };

  const handleRemove = (index: number) => {
    setDroppedPasteLines(0);
    onChange(value.filter((_, i) => i !== index));
  };

  const atItemLimit = value.length >= GUIDELINES_MAX_ITEMS;

  const handleAdd = () => {
    if (atItemLimit) return;
    setDroppedPasteLines(0);
    onChange([...value, '']);
    requestAnimationFrame(() => {
      footerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>, index: number) => {
    const clipboardText = e.clipboardData.getData('text');
    const lines = clipboardText.split('\n').filter((line) => line.trim() !== '');
    if (lines.length <= 1) return;

    e.preventDefault();
    const textarea = e.currentTarget;
    const cursor = textarea.selectionStart;
    const currentText = value[index];
    const textBefore = currentText.substring(0, cursor);
    const textAfter = currentText.substring(textarea.selectionEnd);

    const newEntries: string[] = [];
    newEntries.push(textBefore + lines[0]);
    for (let i = 1; i < lines.length - 1; i++) {
      newEntries.push(lines[i]);
    }
    newEntries.push(lines[lines.length - 1] + textAfter);

    // Clamp inserted rows so existing trailing guidelines are preserved.
    const room = GUIDELINES_MAX_ITEMS - (value.length - 1);
    const inserted = newEntries.slice(0, Math.max(1, room));
    if (inserted.length < newEntries.length) {
      inserted[inserted.length - 1] += textAfter;
    }
    setDroppedPasteLines(newEntries.length - inserted.length);

    const newValue = [...value];
    newValue.splice(index, 1, ...inserted);
    onChange(newValue);

    const lastNewIndex = index + inserted.length - 1;
    focusTextarea(lastNewIndex, inserted[inserted.length - 1].length);
  };

  return (
    <div
      className={cn(
        'rounded-xl border bg-card transition-colors',
        error ? 'border-destructive' : 'border-border',
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      <div className="divide-y divide-border">
        {value.map((command, index) => (
          <div
            key={index}
            className="flex items-start gap-3 py-1.5 px-1 group focus-within:bg-accent/40"
          >
            <span className="w-5 flex-shrink-0 pt-0.5 text-right text-xs font-medium text-foreground-subtle">
              {index + 1}.
            </span>
            <textarea
              data-testid={`guideline-textarea-${index}`}
              value={command}
              onChange={(e) => {
                handleUpdate(index, e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = `${e.target.scrollHeight}px`;
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (atItemLimit) return;
                  const textarea = e.currentTarget;
                  const cursor = textarea.selectionStart;
                  const currentText = value[index];
                  const textBefore = currentText.substring(0, cursor);
                  const textAfter = currentText.substring(cursor);
                  const newValue = [...value];
                  newValue[index] = textBefore;
                  newValue.splice(index + 1, 0, textAfter);
                  setDroppedPasteLines(0);
                  onChange(newValue);
                  focusTextarea(index + 1, 0);
                }
                if (e.key === 'Backspace' && index > 0) {
                  const textarea = e.currentTarget;
                  const cursor = textarea.selectionStart;
                  if (cursor === 0 && textarea.selectionEnd === 0) {
                    e.preventDefault();
                    const prevText = value[index - 1];
                    const currentText = value[index];
                    const newValue = [...value];
                    newValue[index - 1] = prevText + currentText;
                    newValue.splice(index, 1);
                    setDroppedPasteLines(0);
                    onChange(newValue);
                    focusTextarea(index - 1, prevText.length);
                  }
                }
                if (e.key === 'ArrowUp' && index > 0) {
                  e.preventDefault();
                  const cursor = e.currentTarget.selectionStart;
                  const targetLen = value[index - 1].length;
                  focusTextarea(index - 1, Math.min(cursor, targetLen));
                }
                if (e.key === 'ArrowDown' && index < value.length - 1) {
                  e.preventDefault();
                  const cursor = e.currentTarget.selectionStart;
                  const targetLen = value[index + 1].length;
                  focusTextarea(index + 1, Math.min(cursor, targetLen));
                }
              }}
              onPaste={(e) => handlePaste(e, index)}
              disabled={disabled}
              rows={1}
              className="mt-px flex-1 resize-none border-none bg-transparent text-sm leading-relaxed text-foreground outline-none placeholder:text-foreground-subtle"
              ref={(el) => {
                textareaRefs.current[index] = el;
                if (el) {
                  el.style.height = 'auto';
                  el.style.height = `${el.scrollHeight}px`;
                }
              }}
            />
            {countChars(command) > GUIDELINE_MAX_CHARS * WARN_RATIO && (
              <span
                data-testid={`guideline-counter-${index}`}
                className={cn(
                  'flex-shrink-0 pt-1 text-xs tabular-nums',
                  countChars(command) > GUIDELINE_MAX_CHARS ? 'text-destructive' : 'text-warning',
                )}
              >
                {countChars(command)} / {GUIDELINE_MAX_CHARS}
              </span>
            )}
            <Button
              data-testid={`guideline-remove-${index}`}
              type="button"
              onClick={() => handleRemove(index)}
              disabled={disabled}
              aria-label={t('form.commands.remove')}
              variant="custom"
              className="h-auto w-auto flex-shrink-0 rounded-none !border-none bg-transparent p-0 pt-1 pr-1 text-foreground-subtle hover:!bg-transparent hover:text-destructive focus-visible:ring-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>

      {droppedPasteLines > 0 && (
        <div
          data-testid="guideline-paste-truncated"
          role="status"
          className="border-t border-border bg-warning-muted px-3 py-2 text-xs text-warning-foreground"
        >
          {t('form.commands.paste_truncated', {
            count: droppedPasteLines,
            max: GUIDELINES_MAX_ITEMS,
          })}
        </div>
      )}

      {/* Footer */}
      <div
        ref={footerRef}
        className={cn(
          'flex items-center justify-between bg-accent px-3 py-2',
          value.length > 0 ? 'rounded-b-xl border-t border-border' : 'rounded-xl',
        )}
      >
        <Button
          type="button"
          onClick={handleAdd}
          disabled={disabled || atItemLimit}
          variant="secondary"
          size="xs"
        >
          <Plus className="h-3.5 w-3.5" />
          {t('form.commands.add')}
        </Button>
        <span
          data-testid="guideline-total-counter"
          className={cn(
            'text-xs tabular-nums',
            totalChars > GUIDELINES_MAX_TOTAL_CHARS
              ? 'text-destructive'
              : totalChars > GUIDELINES_MAX_TOTAL_CHARS * WARN_RATIO
                ? 'text-warning'
                : 'text-foreground-subtle',
          )}
        >
          {totalChars} / {GUIDELINES_MAX_TOTAL_CHARS} {t('common:characters')}
        </span>
      </div>
    </div>
  );
};

export default AiCommandsList;
